import { Link } from 'react-router-dom';
import { getCategoryName } from '@/mocks/categories';

interface ArticleCardProps {
  title: string;
  slug: string;
  categorySlug: string;
  excerpt: string;
  imageUrl: string;
  author: string;
  publishDate: string;
  readTime: string;
  isNew?: boolean;
  isHot?: boolean;
  views?: number;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({
  title,
  slug,
  categorySlug,
  excerpt,
  imageUrl,
  author,
  publishDate,
  readTime,
  isNew,
  isHot,
  views,
  variant = 'default',
}: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-48 h-36 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
              {getCategoryName(categorySlug)}
            </span>
            {isNew && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                新品
              </span>
            )}
            {isHot && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                热门
              </span>
            )}
          </div>
          <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
            {title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{author}</span>
            <span>·</span>
            <span>{publishDate}</span>
            <span>·</span>
            <span>{readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-16 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
            {title}
          </h4>
          <div className="flex items-center gap-2 text-xs text-foreground-500 mt-1">
            <span>{publishDate}</span>
            <span>·</span>
            <span>{readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group block rounded-lg bg-background-50 border border-background-200/60 overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100/70 text-primary-700 font-medium">
            {getCategoryName(categorySlug)}
          </span>
          {isNew && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100/70 text-accent-700 font-medium">
              新品
            </span>
          )}
          {isHot && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100/70 text-secondary-700 font-medium">
              热门
            </span>
          )}
        </div>
        <h3 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
          {title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{excerpt}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span>{author}</span>
            <span>·</span>
            <span>{publishDate}</span>
            <span>·</span>
            <span>{readTime}</span>
          </div>
          {typeof views === 'number' && (
            <div className="flex items-center gap-1 text-xs text-foreground-400">
              <i className="ri-eye-line"></i>
              <span>{views > 1000 ? `${(views / 1000).toFixed(1)}k` : views}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}